import { useCallback, useEffect, useRef, useState } from "react";
import {
  getNotifications,
  getNotificationUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  createNotificationStreamToken,
  getToken,
} from "../api/client";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

export function useNotifications({ enabled = true, onNotification, onInitialLoad } = {}) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const onNotificationRef = useRef(onNotification);
  const onInitialLoadRef = useRef(onInitialLoad);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    onInitialLoadRef.current = onInitialLoad;
  }, [onInitialLoad]);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        getNotifications({ limit: 50 }),
        getNotificationUnreadCount(),
      ]);
      const list = listRes.notifications || [];
      setNotifications(list);
      setUnreadCount(countRes.count || 0);
      if (!initialLoadDoneRef.current) {
        initialLoadDoneRef.current = true;
        if (typeof onInitialLoadRef.current === "function") {
          onInitialLoadRef.current(list);
        }
      }
    } catch {
      /* keep prior state */
    } finally {
      setLoading(false);
    }
  }, []);

  const scheduleReconnect = useCallback((delayMs, connectStream) => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(() => {
      connectStream();
    }, delayMs);
  }, []);

  const connectStream = useCallback(async () => {
    if (!enabled || !getToken()) return;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    try {
      const { streamToken } = await createNotificationStreamToken();
      const url = `${API_BASE}/users/notifications/stream?token=${encodeURIComponent(streamToken)}`;
      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.addEventListener("notification", (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          let inserted = false;
          setNotifications((prev) => {
            if (prev.some((n) => n.id === payload.id)) return prev;
            inserted = true;
            return [{ ...payload, read: false }, ...prev];
          });
          if (inserted) setUnreadCount((c) => c + 1);
          if (inserted && typeof onNotificationRef.current === "function") {
            onNotificationRef.current({ ...payload, read: false });
          }
        } catch {
          refresh();
        }
      });

      es.onopen = () => {
        reconnectAttemptRef.current = 0;
      };

      es.onerror = () => {
        es.close();
        eventSourceRef.current = null;
        const attempt = reconnectAttemptRef.current + 1;
        reconnectAttemptRef.current = attempt;
        const delay = Math.min(5000 * 2 ** (attempt - 1), 60000);
        scheduleReconnect(delay, connectStream);
      };

      refreshTimerRef.current = setTimeout(() => {
        es.close();
        reconnectAttemptRef.current = 0;
        connectStream();
      }, 50000);
    } catch {
      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      const delay = Math.min(5000 * 2 ** (attempt - 1), 60000);
      scheduleReconnect(delay, connectStream);
    }
  }, [enabled, refresh, scheduleReconnect]);

  useEffect(() => {
    if (!enabled || !getToken()) {
      initialLoadDoneRef.current = false;
      return undefined;
    }
    refresh();
    connectStream();
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      reconnectAttemptRef.current = 0;
      initialLoadDoneRef.current = false;
    };
  }, [enabled, refresh, connectStream]);

  const markRead = useCallback(async (id) => {
    await markNotificationRead(id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  }, []);

  const markAllRead = useCallback(async () => {
    await markAllNotificationsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    refresh,
    markRead,
    markAllRead,
  };
}
